import type { WorkbookFreeformSheet } from "@erp/shared-types";
import type { ColumnType } from "../formula";
import {
  cellAddress,
  parseCellAddress,
  parseCellRange,
  type CellPosition,
} from "./address";

/** One column of a freeform sheet's Result table: its header cell and Declared type. */
export interface FreeformTableColumn {
  /** The header cell's text: the column's name. */
  name: string;
  /** Its header cell's A1 address. */
  header: string;
  /** Its column in the grid (A is 1). */
  column: number;
  type: ColumnType;
  numberFormat?: string;
}

/** A freeform sheet's declared table: its header row, its rows and its columns. */
export interface FreeformTableLayout {
  headerRow: number;
  /** The rows of the table after its header, first and last. */
  firstRow: number;
  lastRow: number;
  columns: FreeformTableColumn[];
}

/**
 * The layout of a freeform sheet's declared table: its range's first row
 * names the columns, each header cell's text as typed; the rows below are
 * its rows. Null without a table or with a range that isn't one.
 */
export function freeformTableLayout(
  sheet: WorkbookFreeformSheet,
): FreeformTableLayout | null {
  const range = sheet.table && parseCellRange(sheet.table.range);
  if (!sheet.table || !range) return null;
  const declared = sheet.table.columns;
  const columns: FreeformTableColumn[] = [];
  for (
    let column = range.start.column;
    column <= range.end.column;
    column += 1
  ) {
    const header = cellAddress({ column, row: range.start.row });
    const type = declared[column - range.start.column];
    columns.push({
      // A leading ' marks text, as in a spreadsheet.
      name: (sheet.cells[header] ?? "").replace(/^'/, "").trim(),
      header,
      column,
      type: type?.type ?? "text",
      ...(type?.numberFormat !== undefined && {
        numberFormat: type.numberFormat,
      }),
    });
  }
  return {
    headerRow: range.start.row,
    firstRow: range.start.row + 1,
    lastRow: range.end.row,
    columns,
  };
}

/** A freeform sheet's filled cells with their positions, in reading order. */
export function filledCells(
  sheet: WorkbookFreeformSheet,
): { address: string; position: CellPosition; input: string }[] {
  return Object.entries(sheet.cells)
    .flatMap(([address, input]) => {
      const position = parseCellAddress(address);
      return position && input !== "" ? [{ address, position, input }] : [];
    })
    .sort(
      (a, b) =>
        a.position.row - b.position.row ||
        a.position.column - b.position.column,
    );
}
